<template>
  <v-container fluid class="pa-0">
    <UniversalTable
      class="responsibilities-directory-table"
      :config="tableConfig.tableParams.value"
      :items="filteredResponsibilities"
      :backendPagination="backendPagination"
      :itemsCount="itemsCount"
      :externalCurrentPage="currentPage"
      :itemsPerPage="itemsPerPage"
      :loading="loading"
      @onSearch="onSearch"
      @onRequest="$emit('onRequest', $event)"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="viewState.clearFilters"
      :emptyObjectData="emptyObjectData"
    >
      <template #loading>
        <SharedTableSkeleton
          :row-count="itemsPerPage || 5"
          :column-count="6"
          column-template="2fr 1.3fr 1.6fr 1.4fr 1.2fr 1.2fr"
        />
      </template>
    </UniversalTable>
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Directory Responsibilities"
      :filterGroups="[
        {
          label: 'Readiness',
          items: [
            { label: 'No Filter', value: 'no_filter' },
            { label: 'Developing', value: 'developing' },
            { label: 'Competent', value: 'competent' },
            { label: 'Expert', value: 'expert' },
          ],
        },
      ]"
      :statusOptions="[]"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
  </v-container>
</template>

<script setup>
import { computed, watch } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import { useTableConfig } from "./composables/useTableConfig";
import { useResponsibilitiesDirectoryTableViewState } from "./composables/useViewState";
import { matchesReadinessFilter } from "@components/EmployeesDirectoryTable/composables/readinessFilter.js";

const props = defineProps({
  dataSource: {
    type: Array,
    default: () => [],
  },
  backendPagination: {
    type: Boolean,
    default: false,
  },
  itemsCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  searchText: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onRequest", "onSearch"]);

const viewState = useResponsibilitiesDirectoryTableViewState();
const tableConfig = useTableConfig(viewState);

watch(
  () => props.searchText,
  (val) => {
    viewState.state.filters.searchText = val ?? "";
  },
  { immediate: true }
);

function onSearch(text) {
  viewState.state.filters.searchText = text ?? "";
  emit("onSearch", text);
}

const emptyObjectData = {
  singular: "Responsibility",
  plural: "Responsibilities",
};

const NO_FILTER_LABEL = "No Filter";
const NO_FILTER_VALUE = "no_filter";

const filteredResponsibilities = computed(() => {
  let responsibilities = [...props.dataSource];

  if (!props.backendPagination) {
    if (
      viewState.state.filters.searchText &&
      viewState.state.filters.searchText.length > 0
    ) {
      const query = viewState.state.filters.searchText.toLowerCase();
      responsibilities = responsibilities.filter((el) => {
        const name = (el.name || "").toLowerCase();
        const codeStr = (el.code_str || "").toLowerCase();
        return name.includes(query) || codeStr.includes(query);
      });
    }
  }

  const filterValues = viewState.state.filters.values;
  if (filterValues.length > 0) {
    const readinessFilter = filterValues[0];
    const isNoFilter =
      readinessFilter === NO_FILTER_LABEL ||
      readinessFilter === NO_FILTER_VALUE;
    if (!isNoFilter) {
      responsibilities = responsibilities.filter((el) =>
        matchesReadinessFilter(
          {
            current_readiness:
              el.avg_readiness ?? el.current_readiness ?? 0,
          },
          readinessFilter
        )
      );
    }
  }

  return responsibilities.sort((a, b) => {
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
});
</script>

<style scoped>
:deep(.filter-button) {
  /* Override SearchBar .filter-button { padding-left: unset !important } */
  padding-left: 16px !important;
}

.responsibilities-directory-table ::v-deep thead th:nth-child(1) .sortable-header-content,
.responsibilities-directory-table ::v-deep thead th:nth-child(1) .d-flex.align-center {
  justify-content: flex-start !important;
}

.responsibilities-directory-table ::v-deep thead th:nth-child(3) .sortable-header-content,
.responsibilities-directory-table ::v-deep thead th:nth-child(3) .d-flex.align-center {
  justify-content: flex-start !important;
}

.responsibilities-directory-table ::v-deep thead th:not(:first-child):not(:nth-child(3)) .sortable-header-content,
.responsibilities-directory-table ::v-deep thead th:not(:first-child):not(:nth-child(3)) .d-flex.align-center {
  justify-content: center !important;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(1) {
  text-align: left !important;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(1) .cell-container {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(2) {
  text-align: center !important;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(2) .cell-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(3) {
  text-align: left !important;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(3) .cell-container {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(n + 4) {
  text-align: center !important;
}

.responsibilities-directory-table ::v-deep tbody td:nth-child(n + 4) .cell-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

:deep(.employee-badge-counter) {
  margin: 0 auto !important;
}

.responsibilities-directory-table ::v-deep tbody tr:hover {
  background-color: transparent !important;
}

::v-deep .employee-readiness-bar {
  left: 0 !important;
}
</style>
